import TrailCard from '@alltrails/cards/components/TrailCard';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import clickFromSection from '../../../utils/PageUI/clickFromSection';
import { type PageUICommunityInsightSection, type PageUITrailResultSection, sectionKeys } from '../../../utils/PageUI/pageUI';
import trailFromPageUISection from '../../../utils/PageUI/trailFromPageUISection';
import { usePageUIActions } from '../PageUIActionContext/PageUIActionContext';
import { usePageUIMapHover } from '../PageUIMapHoverContext/PageUIMapHoverContext';
import PageUIReviewQuote from '../PageUIReviewQuote/PageUIReviewQuote';
import { type PageUISaveableItem, usePageUISave } from '../PageUISaveContext/PageUISaveContext';
import styles from './PageUITrailResult.module.scss';

type Props = {
  /** Community insights about this trail, quoted right under its card. PageUISectionList only passes them when asked to. */
  insights?: PageUICommunityInsightSection[];
  section: PageUITrailResultSection;
};

export default function PageUITrailResult({ insights, section }: Props) {
  const languageRegionCode = useLanguageRegionCode();
  const { onSectionHover, onSectionHoverEnd } = usePageUIMapHover();
  const actions = usePageUIActions();
  const save = usePageUISave();
  const click = clickFromSection(section);
  const saveableItem: PageUISaveableItem = { type: 'trail', id: section.trailId };

  const card = (
    <TrailCard
      blockNavigation={Boolean(actions)}
      className={styles.trailResult}
      isCompleted={save?.isCompleted(saveableItem)}
      isFavorite={save?.isSaved(saveableItem)}
      isVerified={save?.isVerified(saveableItem)}
      languageRegionCode={languageRegionCode}
      onCardClick={actions && (event => actions.onClick(click, event))}
      onCardMouseEnter={() => onSectionHover(section)}
      onCardMouseLeave={onSectionHoverEnd}
      onFavoriteClick={save && (() => save.onSaveClick(saveableItem))}
      openInNewTab={!actions}
      trail={trailFromPageUISection(section)}
      url={actions?.getHref(click.clickAction)}
      variant="small"
    />
  );

  if (!insights?.length) {
    return card;
  }

  const insightKeys = sectionKeys(insights);

  return (
    <div className={styles.withInsights}>
      {card}
      <div className={styles.insights}>
        {insights.map((insight, insightIndex) => (
          <PageUIReviewQuote key={insightKeys[insightIndex]} reviewHighlight={insight.reviewHighlight} />
        ))}
      </div>
    </div>
  );
}
